/**
/**
 * Dashboard API Contracts
 * Feature: 027-request-dashboard
 *
 * TypeScript type definitions for the Employee Request Dashboard.
 * These types align with the backend API contracts defined in docs/API_CONTRACTS.md
 */

// ============================================================================
// Event State Model
// ============================================================================

/**
 * Event lifecycle states - single source of truth for UI rendering
 */
export type EventState =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'CONFIRMED'
  | 'DECLINED'
  | 'SUBMITTED_FOR_CANCELLATION'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'NO_OCCURRENCE';

const ALL_STATES: EventState[] = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'CONFIRMED',
  'DECLINED',
  'SUBMITTED_FOR_CANCELLATION',
  'CANCELLED',
  'COMPLETED',
  'NO_OCCURRENCE',
];

const LEGACY_STATE_ALIASES: Record<string, EventState> = {
  APPROVED: 'CONFIRMED',
  REJECTED: 'DECLINED',
  DENIED: 'DECLINED',
  CANCEL_REQUESTED: 'SUBMITTED_FOR_CANCELLATION',
  NO_SHOW: 'NO_OCCURRENCE',
};

export function normalizeEventState(raw: string): EventState {
  const upper = raw.toUpperCase().trim();
  if (ALL_STATES.includes(upper as EventState)) {
    return upper as EventState;
  }
  const legacyAlias = LEGACY_STATE_ALIASES[upper];
  if (legacyAlias) {
    return legacyAlias;
  }
  return 'DRAFT';
}

// ============================================================================
// API Response Types
// ============================================================================

/** Shared metadata on every API envelope */
interface ApiMeta {
  timestamp: string;
  correlationId: string;
}

/** Shared error payload */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]> | Array<{ field: string; message: string }>;
  target?: string;
  timestamp?: string;
  traceId?: string;
}

/**
 * API error response
 */
export interface ErrorResponse {
  success: false;
  error: ApiError;
  meta?: ApiMeta;
}

/**
 * Standard API response wrapper (discriminated union)
 */
export type ApiResponse<T> = { success: true; data: T; meta?: ApiMeta } | ErrorResponse;

/**
 * Paginated API response wrapper (discriminated union)
 */
export type PaginatedResponse<T> =
  | {
      success: true;
      data: T[];
      pagination: {
        totalCount: number;
        pageSize: number;
        continuationToken?: string;
        hasMore: boolean;
      };
      meta?: ApiMeta;
    }
  | ErrorResponse;

// ============================================================================
// Event Types
// ============================================================================

/**
 * Event summary for dashboard list view
 * Source: GET /api/v1/events
 */
export interface EventSummary {
  eventId: string;
  requestId?: string;
  seriesId?: string;
  /** 1-based position of this event within its multi-day series (API-provided). */
  seriesIndex?: number;
  /** Total number of events in this event's series (API-provided). */
  seriesTotal?: number;
  eventStartDate: string;
  eventEndDate: string;
  eventState: EventState;
  eventTitle?: string;
  buildingId: string;
  buildingName?: string;
  numberOfAttendees: number;
  spaceId?: string;
  spaceName?: string;
  space?: {
    spaceId?: string;
    spaceName?: string;
    spaceEmail?: string;
    selectedSetupCodes?: string[];
  };
  /**
   * Multi-space bookings: canonical list of spaces reserved for the event.
   * Present when a planner assigns more than one space; falls back to the
   * singular `space` object for legacy single-space events.
   */
  spaces?: Array<{
    spaceId?: string;
    spaceName?: string;
    spaceEmail?: string;
    selectedSetupCodes?: string[];
  }>;
  calendarBookingId?: string;
  cancelledReason?: string;
  denialReason?: string;
  submittedBy?: string;
  submittedByEmail?: string;
  sponsor?: {
    name?: string;
    email?: string;
    costCenter?: string;
  };
  onSiteContacts?: Array<{
    name?: string;
    email?: string;
    phone?: string;
  }>;
  submittedDate?: string;
  createdBy: string;
  createdAt: string;
  lastUpdatedAt: string;
  lastUpdatedBy?: string;
  currentStep?: string;
}

/**
 * Get events query parameters
 */
export interface GetEventsParams {
  status?: string;
  tab?: 'active' | 'past';
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  pageSize?: number;
  continuationToken?: string;
  sortBy?: 'eventDate' | 'eventStartDate' | 'createdAt' | 'lastUpdatedAt';
  sortOrder?: 'asc' | 'desc';
  groupBySeries?: boolean;
}

/**
 * Get events response
 */
export type GetEventsResponse = PaginatedResponse<EventSummary>;

// ============================================================================
// Event Actions
// ============================================================================

/**
 * Event action request payload
 * Source: POST /api/v1/events/{eventId}/actions
 */
export interface EventActionRequest {
  action: 'SUBMITTED_FOR_CANCELLATION';
  comments: string;
}

/**
 * Event action response
 */
export interface EventActionResponse {
  success: true;
  data: {
    eventId: string;
    previousState: EventState;
    newState: EventState;
    actionedBy: string;
    actionedAt: string;
    message: string;
  };
}

// ============================================================================
// Dashboard UI Types
// ============================================================================

/**
 * Dashboard tab identifiers
 */
export type DashboardTab = 'draft' | 'current' | 'past';

export interface BookingsTabQueryConfig {
  tab: DashboardTab;
  pageSize: 10;
  sortBy: 'eventStartDate';
  sortOrder: SortOrder;
  requestShape: GetEventsParams;
}

export interface BookingsTabPageCache {
  currentPage: number;
  totalCount: number | null;
  pageCount: number;
  pages: Record<number, EventSummary[]>;
  tokensByPageStart: Record<number, string | null>;
  lastKnownContinuationToken: string | null;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

export type BookingsPrefetchStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface BookingsPaginationSession {
  activeTab: DashboardTab;
  defaultTab: 'current';
  tabs: Record<DashboardTab, BookingsTabPageCache>;
  prefetchStatus: Record<DashboardTab, BookingsPrefetchStatus>;
}

export interface BookingsPaginationViewModel {
  currentPage: number;
  pageCount: number;
  visiblePages: number[];
  canGoPrevious: boolean;
  canGoNext: boolean;
  ariaLabel: string;
}

/**
 * State to section mapping
 */
export const STATE_TO_SECTION: Record<EventState, DashboardTab> = {
  DRAFT: 'draft',
  SUBMITTED: 'current',
  UNDER_REVIEW: 'current',
  CONFIRMED: 'current',
  SUBMITTED_FOR_CANCELLATION: 'current',
  DECLINED: 'past',
  COMPLETED: 'past',
  CANCELLED: 'past',
  NO_OCCURRENCE: 'past',
};

export const BOOKING_TAB_API_STATES: Record<DashboardTab, EventState[]> = {
  draft: ['DRAFT'],
  current: [
    'SUBMITTED',
    'UNDER_REVIEW',
    'CONFIRMED',
    'SUBMITTED_FOR_CANCELLATION',
    'CANCELLED',
    'DECLINED',
  ],
  past: ['DECLINED', 'COMPLETED', 'CANCELLED', 'NO_OCCURRENCE'],
};

export function getBookingTabStatusFilter(tab: DashboardTab): string {
  return BOOKING_TAB_API_STATES[tab].join(',');
}

/**
 * States that allow cancellation request
 */
export const CANCELLABLE_STATES: EventState[] = ['SUBMITTED', 'UNDER_REVIEW', 'CONFIRMED'];

/**
 * States that allow deletion (drafts only)
 */
export const DELETABLE_STATES: EventState[] = ['DRAFT'];

/**
 * States that show decline reason
 */
export const REASON_VIEWABLE_STATES: EventState[] = [
  'DECLINED',
  'CANCELLED',
  'SUBMITTED_FOR_CANCELLATION',
];

/**
 * States that show a disabled cancel option in the menu
 */
export const CANCEL_DISABLED_STATES: EventState[] = ['CANCELLED', 'SUBMITTED_FOR_CANCELLATION'];

/**
 * Derive available actions from event state
 */
export interface EventActions {
  canView: boolean;
  canDelete: boolean;
  canCancel: boolean;
  canViewReason: boolean;
  showCancelDisabled: boolean;
}

export function getEventActions(state: EventState): EventActions {
  return {
    canView: true,
    canDelete: DELETABLE_STATES.includes(state),
    canCancel: CANCELLABLE_STATES.includes(state),
    canViewReason: REASON_VIEWABLE_STATES.includes(state),
    showCancelDisabled: CANCEL_DISABLED_STATES.includes(state),
  };
}

/**
 * Group events by dashboard section.
 * Events whose state maps to "current" but whose eventEndDate is in the past
 * are placed in the "past" bucket so completed events don't stay in the
 * active list.
 */
export function groupEventsBySection(events: EventSummary[]): Record<DashboardTab, EventSummary[]> {
  const now = new Date();
  return events.reduce(
    (acc, event) => {
      let section = STATE_TO_SECTION[event.eventState];
      if (section === 'current' && event.eventEndDate && new Date(event.eventEndDate) < now) {
        section = 'past';
      }
      // Reverse: cancelled/declined events whose eventEndDate is still in the
      // future stay in "current" — tab placement is date-driven, not state-driven.
      if (
        section === 'past' &&
        event.eventEndDate &&
        new Date(event.eventEndDate) >= now &&
        event.eventState !== 'COMPLETED' &&
        event.eventState !== 'NO_OCCURRENCE'
      ) {
        section = 'current';
      }
      if (section) {
        acc[section].push(event);
      }
      return acc;
    },
    { draft: [], current: [], past: [] } as Record<DashboardTab, EventSummary[]>
  );
}

// ============================================================================
// Status Display
// ============================================================================

/**
 * Status badge appearance
 */
export type StatusBadgeAppearance = 'neutral' | 'informative' | 'success' | 'warning' | 'danger';

/**
 * Status display configuration
 */
export interface StatusDisplayConfig {
  appearance: StatusBadgeAppearance;
  labelKey: string;
}

export const STATUS_DISPLAY: Record<EventState, StatusDisplayConfig> = {
  DRAFT: { appearance: 'neutral', labelKey: 'dashboard.status.draft' },
  SUBMITTED: { appearance: 'informative', labelKey: 'dashboard.status.submitted' },
  UNDER_REVIEW: { appearance: 'informative', labelKey: 'dashboard.status.underReview' },
  CONFIRMED: { appearance: 'success', labelKey: 'dashboard.status.confirmed' },
  DECLINED: { appearance: 'danger', labelKey: 'dashboard.status.declined' },
  SUBMITTED_FOR_CANCELLATION: {
    appearance: 'warning',
    labelKey: 'dashboard.status.submittedForCancellation',
  },
  COMPLETED: { appearance: 'success', labelKey: 'dashboard.status.completed' },
  CANCELLED: { appearance: 'neutral', labelKey: 'dashboard.status.cancelled' },
  NO_OCCURRENCE: { appearance: 'danger', labelKey: 'dashboard.status.noOccurrence' },
};

// ============================================================================
// Component Props
// ============================================================================

/**
 * StatusTag component props
 */
export interface StatusTagProps {
  state: EventState;
}

/**
 * RequestCard component props
 */
export interface RequestCardProps {
  event: EventSummary;
  onView: (eventId: string) => void;
  onDelete?: ((eventId: string) => void) | undefined;
  onCancel?: ((eventId: string) => void) | undefined;
  onViewReason?: ((eventId: string) => void) | undefined;
  /**
   * Opt-in to the Figma 2026 visual style (white surface, neutral border,
   * 8 px radius). Defaults to `false` so Planner/Admin call sites render
   * the legacy look unchanged.
   */
  figmaVariant?: boolean;
}

/**
 * RequestList component props
 */
export interface RequestListProps {
  events: EventSummary[];
  loading: boolean;
  error?: string | undefined;
  onLoadMore?: (() => void | Promise<void>) | undefined;
  hasMore: boolean;
  onView: (eventId: string) => void;
  onDelete?: ((seriesId: string) => void) | undefined;
  onCancel?: ((eventId: string) => void | Promise<void>) | undefined;
  /** Forwarded to `RequestCard` and `EmptyState` for the Figma 2026 visual style. */
  figmaVariant?: boolean;
}

/**
 * DashboardTabs component props
 */
export interface DashboardTabsProps {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
  draftCount: number;
  currentCount: number;
  pastCount: number;
  /** Opt-in to the Figma 2026 underline-style tabs with neutral count chips. */
  figmaVariant?: boolean;
}

/**
 * EmptyState component props
 */
export interface EmptyStateProps {
  tab: DashboardTab;
  onCreateRequest?: (() => void) | undefined;
  /** Opt-in to the Figma 2026 visual style (soft-tinted illustration block). */
  figmaVariant?: boolean;
}

/**
 * Sorting configuration
 */
export type SortField = 'eventStartDate' | 'createdAt' | 'lastUpdatedAt';
export type SortOrder = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  order: SortOrder;
}
